import { fetchAuthSession } from 'aws-amplify/auth';

const API_BASE_URL = import.meta.env.VITE_API_GATEWAY_URL || '';

export interface CognitoUser {
  id: string;
  username: string;
  email: string;
  name: string;
  groups: string[];
  role: 'admin' | 'instructor' | 'student';
  status: 'CONFIRMED' | 'UNCONFIRMED' | 'FORCE_CHANGE_PASSWORD' | 'RESET_REQUIRED';
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateUserInput {
  email: string;
  name: string;
  temporaryPassword: string;
  group: 'Admin' | 'Trainer' | 'Student' | 'Mentors';
}

export interface UpdateUserInput {
  userId: string;
  name?: string;
  group?: 'Admin' | 'Trainer' | 'Student' | 'Mentors';
  enabled?: boolean;
}

class AdminService {
  // Get auth token for API calls
  private async getAuthToken(): Promise<string> {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (!token) {
        throw new Error('No authentication token found');
      }
      return token;
    } catch (error) {
      throw new Error('Failed to get authentication token');
    }
  }

  // Check if user is admin
  async checkAdminAccess(): Promise<boolean> {
    try {
      const session = await fetchAuthSession();
      const groups = session.tokens?.accessToken?.payload['cognito:groups'] as string[] || [];
      return groups.includes('Admin');
    } catch (error) {
      console.error('Error checking admin access:', error);
      return false;
    }
  }

  // Check if user is admin or trainer
  async checkAdminOrTrainerAccess(): Promise<boolean> {
    try {
      const session = await fetchAuthSession();
      const groups = session.tokens?.accessToken?.payload['cognito:groups'] as string[] || [];
      return groups.includes('Admin') || groups.includes('Trainer') || groups.includes('Instructor') || groups.includes('Trainers');
    } catch (error) {
      console.error('Error checking admin/trainer access:', error);
      return false;
    }
  }

  // List all users from Cognito via API (requires admin or trainer)
  async listUsers(): Promise<CognitoUser[]> {
    const isAuthorized = await this.checkAdminOrTrainerAccess();
    if (!isAuthorized) {
      throw new Error('Unauthorized: Admin or Trainer access required');
    }

    try {
      const token = await this.getAuthToken();
      
      const response = await fetch(`${API_BASE_URL}/user?action=list`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to list users');
      }

      const data = await response.json();
      return data.users || [];
    } catch (error: any) {
      console.error('Error listing users:', error);
      throw new Error(error.message || 'Failed to list users');
    }
  }

  // List assignable users (trainers/admins) - no admin check required
  async listAssignableUsers(): Promise<CognitoUser[]> {
    try {
      const token = await this.getAuthToken();
      
      const response = await fetch(`${API_BASE_URL}/user?action=list`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        // Fall back to empty list if unauthorized
        console.warn('Failed to fetch assignable users:', response.status);
        return [];
      }

      const data = await response.json();
      return data.users || [];
    } catch (error: any) {
      console.error('Error listing assignable users:', error);
      return [];
    }
  }

  // Create new user in Cognito via API (Admin or Trainer)
  async createUser(input: CreateUserInput): Promise<CognitoUser> {
    const isAuthorized = await this.checkAdminOrTrainerAccess();
    if (!isAuthorized) {
      throw new Error('Unauthorized: Admin or Trainer access required');
    }

    try {
      // Validate input
      if (!input.email || !input.name || !input.temporaryPassword) {
        throw new Error('Email, name, and temporary password are required');
      }

      if (input.temporaryPassword.length < 8) {
        throw new Error('Temporary password must be at least 8 characters');
      }

      const token = await this.getAuthToken();

      const response = await fetch(`${API_BASE_URL}/user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'create',
          email: input.email,
          name: input.name,
          temporaryPassword: input.temporaryPassword,
          group: input.group,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create user');
      }

      const data = await response.json();
      return data.user;
    } catch (error: any) {
      console.error('Error creating user:', error);
      throw new Error(error.message || 'Failed to create user');
    }
  }

  // Update user in Cognito via API (Admin or Trainer)
  async updateUser(input: UpdateUserInput): Promise<CognitoUser> {
    const isAuthorized = await this.checkAdminOrTrainerAccess();
    if (!isAuthorized) {
      throw new Error('Unauthorized: Admin or Trainer access required');
    }

    try {
      const token = await this.getAuthToken();

      const response = await fetch(`${API_BASE_URL}/user`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'update',
          userId: input.userId,
          name: input.name,
          group: input.group,
          enabled: input.enabled,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update user');
      }

      const data = await response.json();
      return data.user;
    } catch (error: any) {
      console.error('Error updating user:', error);
      throw new Error(error.message || 'Failed to update user');
    }
  }

  // Delete user from Cognito via API
  async deleteUser(userId: string, username: string): Promise<void> {
    const isAdmin = await this.checkAdminAccess();
    if (!isAdmin) {
      throw new Error('Unauthorized: Admin access required');
    }

    try {
      const token = await this.getAuthToken();

      const response = await fetch(`${API_BASE_URL}/user`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'delete',
          userId,
          username,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to delete user');
      }
    } catch (error: any) {
      console.error('Error deleting user:', error);
      throw new Error(error.message || 'Failed to delete user');
    }
  }

  // Enable/Disable user via API
  async toggleUserStatus(userId: string, username: string, enable: boolean): Promise<void> {
    const isAdmin = await this.checkAdminAccess();
    if (!isAdmin) {
      throw new Error('Unauthorized: Admin access required');
    }

    try {
      const token = await this.getAuthToken();

      const response = await fetch(`${API_BASE_URL}/user`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: enable ? 'enable' : 'disable',
          userId,
          username,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to toggle user status');
      }
    } catch (error: any) {
      console.error('Error toggling user status:', error);
      throw new Error(error.message || 'Failed to toggle user status');
    }
  }

  // Local storage cache keys for offline & instant reactivity
  private ENROLLMENTS_CACHE_KEY = 'cb_lms_course_enrollments';

  getLocalEnrollmentsMap(): Record<string, string[]> {
    if (typeof localStorage === 'undefined') return {};
    try {
      const data = localStorage.getItem(this.ENROLLMENTS_CACHE_KEY);
      return data ? JSON.parse(data) : {};
    } catch {
      return {};
    }
  }

  saveLocalEnrollmentsMap(map: Record<string, string[]>): void {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(this.ENROLLMENTS_CACHE_KEY, JSON.stringify(map));
    } catch (e) {
      console.warn('Failed to save local enrollments map:', e);
    }
  }

  // Assign a course to a student (Admin or Trainer)
  async assignCourse(userId: string, courseId: string): Promise<void> {
    const isAuthorized = await this.checkAdminOrTrainerAccess();
    if (!isAuthorized) {
      throw new Error('Unauthorized: Admin or Trainer access required');
    }

    // Always update local cache immediately
    const map = this.getLocalEnrollmentsMap();
    const existing = map[userId] || [];
    if (!existing.includes(courseId)) {
      map[userId] = [...existing, courseId];
      this.saveLocalEnrollmentsMap(map);
    }

    try {
      const token = await this.getAuthToken();

      const response = await fetch(`${API_BASE_URL}/enrollments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'assign',
          userId,
          courseId,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        console.warn('Cloud assignCourse warning:', error.message);
      }
    } catch (error: any) {
      console.warn('Error assigning course to cloud API, local cache updated:', error);
    }
  }

  // Unassign a course from a student (Admin or Trainer)
  async unassignCourse(userId: string, courseId: string): Promise<void> {
    const isAuthorized = await this.checkAdminOrTrainerAccess();
    if (!isAuthorized) {
      throw new Error('Unauthorized: Admin or Trainer access required');
    }

    // Update local cache immediately
    const map = this.getLocalEnrollmentsMap();
    const existing = map[userId] || [];
    map[userId] = existing.filter(id => id !== courseId);
    this.saveLocalEnrollmentsMap(map);

    try {
      const token = await this.getAuthToken();

      const response = await fetch(`${API_BASE_URL}/enrollments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'unassign',
          userId,
          courseId,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        console.warn('Cloud unassignCourse warning:', error.message);
      }
    } catch (error: any) {
      console.warn('Error unassigning course from cloud API, local cache updated:', error);
    }
  }

  // List courses assigned to a user
  async getUserCourses(userId: string): Promise<any[]> {
    const isAuthorized = await this.checkAdminOrTrainerAccess();
    if (!isAuthorized) {
      throw new Error('Unauthorized: Admin or Trainer access required');
    }

    try {
      const token = await this.getAuthToken();

      const response = await fetch(`${API_BASE_URL}/enrollments?action=list-user-courses&userId=${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const enrollments = data.enrollments || [];
        // Sync to local cache
        const map = this.getLocalEnrollmentsMap();
        map[userId] = enrollments.map((e: any) => e.courseId);
        this.saveLocalEnrollmentsMap(map);
        return enrollments;
      }
    } catch (error: any) {
      console.warn('Cloud getUserCourses failed, using local cache:', error);
    }

    // Fallback to local cache
    const map = this.getLocalEnrollmentsMap();
    const courseIds = map[userId] || [];
    return courseIds.map(courseId => ({
      userId,
      courseId,
      enrolledAt: new Date().toISOString(),
      status: 'active'
    }));
  }

  // List all student userIds enrolled in a specific course
  async getCourseStudents(courseId: string): Promise<string[]> {
    try {
      const token = await this.getAuthToken();

      const response = await fetch(`${API_BASE_URL}/enrollments?action=list-course-students&courseId=${courseId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const enrollments = data.enrollments || [];
        const studentIds = enrollments.map((e: any) => e.userId || e.id).filter(Boolean);
        if (studentIds.length > 0) {
          // Merge with local map
          const map = this.getLocalEnrollmentsMap();
          studentIds.forEach((uid: string) => {
            const userCourses = map[uid] || [];
            if (!userCourses.includes(courseId)) {
              map[uid] = [...userCourses, courseId];
            }
          });
          this.saveLocalEnrollmentsMap(map);
          return studentIds;
        }
      }
    } catch (error) {
      console.warn(`[AdminService] Cloud getCourseStudents failed for ${courseId}:`, error);
    }

    // Fallback: check local map for all users who have this courseId
    const map = this.getLocalEnrollmentsMap();
    const enrolledUserIds: string[] = [];
    Object.entries(map).forEach(([uid, courses]) => {
      if (courses.includes(courseId)) {
        enrolledUserIds.push(uid);
      }
    });

    return enrolledUserIds;
  }

  // Bulk assign a course to multiple students (Admin or Trainer)
  async bulkAssignCourse(userIds: string[], courseId: string): Promise<any> {
    const isAuthorized = await this.checkAdminOrTrainerAccess();
    if (!isAuthorized) {
      throw new Error('Unauthorized: Admin or Trainer access required');
    }

    // Update local cache immediately
    const map = this.getLocalEnrollmentsMap();
    userIds.forEach(uid => {
      const existing = map[uid] || [];
      if (!existing.includes(courseId)) {
        map[uid] = [...existing, courseId];
      }
    });
    this.saveLocalEnrollmentsMap(map);

    try {
      const token = await this.getAuthToken();

      const response = await fetch(`${API_BASE_URL}/enrollments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'bulk-assign',
          userIds,
          courseId,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to bulk assign course');
      }

      return await response.json();
    } catch (error: any) {
      console.error('Error bulk assigning course:', error);
      throw new Error(error.message || 'Failed to bulk assign course');
    }
  }

  // Get all student tags from Cloud DynamoDB
  async getStudentTags(): Promise<Record<string, string[]>> {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${API_BASE_URL}/user?action=get-student-tags`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        console.warn('Failed to fetch student tags from backend:', response.status);
        return {};
      }

      const data = await response.json();
      return data.tagsMap || {};
    } catch (error: any) {
      console.warn('Error getting student tags from cloud API:', error);
      return {};
    }
  }

  // Save all student tags to Cloud DynamoDB
  async saveStudentTags(tagsMap: Record<string, string[]>): Promise<void> {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${API_BASE_URL}/user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'save-student-tags',
          tagsMap,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        console.warn('Failed to save student tags to cloud API:', error);
      }
    } catch (error: any) {
      console.warn('Error saving student tags to cloud API:', error);
    }
  }
}

export const adminService = new AdminService();

